import { useMutation, useQueryClient } from 'react-query';

import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';
import { QueryEntity } from '@/api/types';

export interface ReferencesDict {
  [key: string]: { id: string; type: 'fact' | 'chunk' };
}

interface CreateManualThreadParams {
  caseId: string;
  question: string;
  answer: string;
  references: ReferencesDict;
}

interface CreateManualThreadResponse {
  threadId: string;
  userMessageId: string;
  assistantMessageId: string;
}

// Transform answer from [1] format to [1]{uuid} format
const transformAnswerWithUUIDs = (answer: string, references: ReferencesDict): string => {
  return answer.replace(/\[(\d+)\]/g, (match, num) => {
    const ref = references[num];
    if (ref?.id) {
      return `[${num}]{${ref.id}}`;
    }
    return match;
  });
};

export const useCreateManualThread = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<CreateManualThreadResponse, Error, CreateManualThreadParams>(
    async (params) => {
      // Transform answer to include UUIDs
      const transformedAnswer = transformAnswerWithUUIDs(params.answer, params.references);

      const fetchConfig = getFetchConfig({
        method: FetchMethod.POST,
        data: {
          question: params.question,
          answer: transformedAnswer,
          references: params.references,
        },
      });

      const response = await fetch(`${APIBaseChronos}/api/case/${params.caseId}/thread/manual`, fetchConfig);

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create manual thread');
      }

      return response.json();
    },
    {
      onSuccess: (data, variables) => {
        // Invalidate threads query to refresh list
        queryClient.invalidateQueries([QueryEntity.Threads, variables.caseId]);
      },
    },
  );
};
